/**
 * KPI tile formatters shared by the Orders and Staged Orders strips and
 * their mobile cards — one tier/abbreviation rule and ONE currency-symbol
 * resolution, so a non-USD figure never renders as `EUR1.50K` anywhere.
 */
import {
  abbreviateNumber,
  abbreviateNumberValue,
  formatNumber,
  formatNumberToDecimal,
} from './numberUtils';

/** Empty-tile glyph shared by every KPI surface. */
export const KPI_EMPTY_VALUE = '—';

/** Currency SYMBOL for a code (`EUR` → `€`), resolved through Intl.
 *  Memoised per code — a strip formats six tiles per render. Falls back
 *  to the code itself for anything Intl rejects. */
const symbolByCurrency = new Map<string, string>();
export function currencySymbol(currency: string): string {
  const cached = symbolByCurrency.get(currency);
  if (cached !== undefined) return cached;
  let symbol = currency;
  try {
    const part = new Intl.NumberFormat('en-US', { style: 'currency', currency })
      .formatToParts(0)
      .find((p) => p.type === 'currency');
    if (part) symbol = part.value;
  } catch {
    // Unknown ISO code — keep the code as the symbol.
  }
  symbolByCurrency.set(currency, symbol);
  return symbol;
}

/** `1234` → `1.23K`; sub-thousand values stay whole. */
export function formatKpiInteger(n: number): string {
  if (!Number.isFinite(n)) return KPI_EMPTY_VALUE;
  if (Math.abs(n) < 1000) return String(Math.round(n));
  return abbreviateNumberValue(n, 2);
}

/**
 * `$42` → `$42.00`, `$1,500` → `$1.50K`, `$2,740,000` → `$2.74Mn` —
 * sub-thousand values keep the full symbol + two decimals so a tile's
 * shape never changes.
 */
export function formatKpiMoney(amount: number, currency: string): string {
  if (!Number.isFinite(amount)) return KPI_EMPTY_VALUE;
  const symbol = currencySymbol(currency);
  if (Math.abs(amount) < 1000) return `${symbol}${amount.toFixed(2)}`;
  return abbreviateNumber(amount, symbol, 2);
}

/** Display value + optional hover detail: `full` is set only when the
 *  display was abbreviated (≥ 1,000). */
export type KpiTileValue = { display: string; full?: string };

export function integerTileValue(n: number): KpiTileValue {
  const display = formatKpiInteger(n);
  if (!Number.isFinite(n) || Math.abs(n) < 1000) return { display };
  return { display, full: formatNumber(Math.round(n)) };
}

export function moneyTileValue(amount: number, currency: string): KpiTileValue {
  const display = formatKpiMoney(amount, currency);
  if (!Number.isFinite(amount) || Math.abs(amount) < 1000) return { display };
  return { display, full: `${currencySymbol(currency)}${formatNumberToDecimal(amount, 2)}` };
}
